import { useMemo, useState } from "react";
import { ResourceName, DataAuditRecord } from "@/types";
import { Box, ButtonBase, CircularProgress, Collapse, Paper, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useGetList, useGetOne, useRecordContext, useResourceContext } from "react-admin";
import { useAppTheme } from "@/hooks/useAppTheme";
import { BORDER_RADIUS } from "@/constants/layout";
import { AuditFieldChange, diffAuditRecord, summariseChanges } from "./auditDiff";
import { AuditRawJson, AuditValuePair } from "./auditValue";

// ─── Resource → PostgreSQL table name mapping ────────────────────────────────

const RESOURCE_TO_TABLE: Record<string, string> = {
  [ResourceName.CLIENTS]: "clients",
  [ResourceName.POLICIES]: "policies",
  [ResourceName.CLAIMANTS]: "claimants",
  [ResourceName.STOPLOSS_CLAIMS]: "stoploss_claims",
  [ResourceName.CLAIM_ITEMS]: "claim_items",
  [ResourceName.VENDORS]: "vendors",
  [ResourceName.TASKS]: "tasks",
  [ResourceName.DOCUMENTS]: "documents",
  [ResourceName.REIMBURSEMENTS]: "reimbursements",
  [ResourceName.ENDORSEMENTS]: "endorsements",
  [ResourceName.USERS]: "users",
  [ResourceName.PARTNERS]: "partners",
};

// ─── Action styling ──────────────────────────────────────────────────────────

const ACTION_DOT_COLORS: Record<string, string> = {
  INSERT: "#22c55e",
  UPDATE: "#3b82f6",
  SOFT_DELETE: "#ef4444",
  DELETE: "#ef4444",
};

const ACTION_LABELS: Record<string, string> = {
  INSERT: "Created",
  UPDATE: "Updated",
  SOFT_DELETE: "Deleted",
  DELETE: "Hard Delete",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

/** Resolves a principal UUID to a user display name. */
const PrincipalName = ({ value }: { value: string }) => {
  const isUuid = UUID_REGEX.test(value);
  const { data } = useGetOne("users", { id: value }, { enabled: isUuid });

  if (!isUuid) return <>{value}</>;
  if (data) return <>{data.full_name || data.email || value}</>;
  return <>{value}</>;
};

// ─── Changed-field detail ────────────────────────────────────────────────────

/**
 * One changed column. Scalars print their `before → after` inline; JSONB blob
 * columns print their changed leaf paths instead of the whole document, with
 * the raw old/new available behind a toggle for investigation.
 */
const ChangedField = ({ change, isDarkMode }: { change: AuditFieldChange; isDarkMode: boolean }) => {
  const [showRaw, setShowRaw] = useState(false);

  if (change.kind === "scalar") {
    return (
      <Box sx={{ display: "flex", gap: 1, alignItems: "baseline", flexWrap: "wrap" }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ minWidth: 132 }}>
          {change.label}
        </Typography>
        <AuditValuePair before={change.before} after={change.after} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "baseline", flexWrap: "wrap" }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ minWidth: 132 }}>
          {change.label}
        </Typography>
        <ButtonBase
          onClick={() => setShowRaw((v) => !v)}
          sx={{ borderRadius: 0.5, px: 0.5, typography: "caption", color: "primary.main" }}
        >
          {showRaw ? "Hide raw" : "View raw"}
        </ButtonBase>
      </Box>

      <Box
        sx={{
          pl: 1.5,
          borderLeft: "2px solid",
          borderColor: "divider",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {change.leaves?.map((leaf) => (
          <Box key={leaf.path} sx={{ display: "flex", gap: 1, alignItems: "baseline", flexWrap: "wrap" }}>
            <Typography variant="caption" color="text.disabled" sx={{ minWidth: 132, wordBreak: "break-word" }}>
              {leaf.path}
            </Typography>
            <AuditValuePair before={leaf.before} after={leaf.after} />
          </Box>
        ))}
        {!!change.hiddenLeafCount && (
          <Typography variant="caption" color="text.disabled" fontStyle="italic">
            +{change.hiddenLeafCount} more change{change.hiddenLeafCount === 1 ? "" : "s"}
          </Typography>
        )}
      </Box>

      <Collapse in={showRaw} unmountOnExit>
        <Box sx={{ display: "flex", gap: 1.5, pl: 1.5, pt: 0.5, flexWrap: "wrap" }}>
          <AuditRawJson label="Before" value={change.before} isDarkMode={isDarkMode} />
          <AuditRawJson label="After" value={change.after} isDarkMode={isDarkMode} />
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── Timeline row ────────────────────────────────────────────────────────────

const AuditEventRow = ({
  record,
  resource,
  isLast,
  isDarkMode,
}: {
  record: DataAuditRecord;
  resource?: string;
  isLast: boolean;
  isDarkMode: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const changes = useMemo(() => diffAuditRecord(record, resource), [record, resource]);

  const actionLabel = ACTION_LABELS[record.action] ?? record.action;

  // `null` means the row carries no old/new pair (INSERT / DELETE) — there is
  // nothing to diff. An empty array means the row *was* rewritten but only the
  // suppressed bookkeeping columns moved; that is reported rather than hidden,
  // so the timeline never silently drops an audit event.
  const isNoOp = changes !== null && changes.length === 0;
  const hasDetail = !!changes?.length;

  const dotColor = isNoOp ? "#9ca3af" : (ACTION_DOT_COLORS[record.action] ?? "#9ca3af");

  const headline = hasDetail ? `${actionLabel} · ${summariseChanges(changes)}` : actionLabel;

  return (
    <Box sx={{ display: "flex", gap: 1.5, position: "relative", pb: isLast ? 0 : 1.5 }}>
      {/* Dot + connector line */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 12, flexShrink: 0, pt: 0.5 }}>
        <Box
          sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dotColor, flexShrink: 0, opacity: isNoOp ? 0.5 : 1 }}
        />
        {!isLast && <Box sx={{ width: 1.5, flex: 1, bgcolor: isDarkMode ? "grey.700" : "grey.300", mt: 0.5 }} />}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          component={hasDetail ? ButtonBase : "div"}
          {...(hasDetail
            ? {
                onClick: () => setExpanded((v) => !v),
                "aria-expanded": expanded,
                "aria-label": `${headline} — ${expanded ? "hide" : "show"} changed fields`,
              }
            : {})}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            width: "100%",
            textAlign: "left",
            justifyContent: "flex-start",
            borderRadius: 0.5,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={500}
            color={isNoOp ? "text.disabled" : "text.primary"}
            sx={{ lineHeight: 1.3, minWidth: 0 }}
          >
            {headline}
          </Typography>
          {hasDetail && (
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                color: "text.disabled",
                transition: "transform 150ms",
                transform: expanded ? "rotate(180deg)" : "none",
              }}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1, mt: 0.25, flexWrap: "wrap" }}>
          {record.principal && (
            <Typography variant="caption" color="text.secondary">
              <PrincipalName value={record.principal} />
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled">
            {formatDate(record.created_datetime)}
          </Typography>
          {isNoOp && (
            <Typography variant="caption" color="text.disabled" fontStyle="italic">
              no field changes
            </Typography>
          )}
        </Box>

        {hasDetail && (
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 0.75, mb: 0.5 }}>
              {changes.map((change) => (
                <ChangedField key={change.key} change={change} isDarkMode={isDarkMode} />
              ))}
            </Box>
          </Collapse>
        )}
      </Box>
    </Box>
  );
};

// ─── Audit timeline ──────────────────────────────────────────────────────────

const AuditTimeline = ({ tableName, recordId }: { tableName: string; recordId: string }) => {
  const { isDarkMode } = useAppTheme();
  const pgTable = RESOURCE_TO_TABLE[tableName] ?? tableName;

  const { data, isLoading } = useGetList<DataAuditRecord>("data-audit", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "created_datetime", order: "DESC" },
    filter: { table_name: pgTable, record_id: recordId },
  });

  const records = useMemo(
    () =>
      [...(data ?? [])].sort((a, b) => new Date(b.created_datetime).getTime() - new Date(a.created_datetime).getTime()),
    [data]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!records.length) {
    return (
      <Typography variant="body2" color="text.disabled" fontStyle="italic" sx={{ py: 1 }}>
        No events recorded yet.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {records.map((record, idx) => (
        <AuditEventRow
          key={record.id}
          record={record}
          resource={tableName}
          isLast={idx === records.length - 1}
          isDarkMode={isDarkMode}
        />
      ))}
    </Box>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

/**
 * A record's change history from the trigger-populated `data_audit` table, as
 * one white card: a "Created … by …" line, then the timeline (dot, action and
 * changed fields, actor and time, raw before/after behind a toggle). A deleted
 * record (TaskShow) adds "Deleted … by …" to the line. Restyled with the
 * stop-loss claim page redesign (#7579); the timeline data and row behaviour
 * are unchanged.
 *
 * @param tableName  React Admin resource name (e.g. `ResourceName.CLIENTS`).
 *                   Falls back to `useResourceContext()` when omitted.
 */
export const AuditLogBox = ({ tableName }: { tableName?: string }) => {
  const resource = useResourceContext();
  const record = useRecordContext();
  const resolvedTableName = tableName || resource;

  const created = record?.created_datetime ? formatDate(record.created_datetime as string) : null;
  const createdBy = record?.created_by as string | undefined;
  const modified = record?.modified_datetime ? formatDate(record.modified_datetime as string) : null;
  const modifiedBy = record?.modified_by as string | undefined;
  const deleted = record?.deleted && record?.deleted_datetime ? formatDate(record.deleted_datetime as string) : null;
  const deletedBy = record?.deleted_by as string | undefined;

  return (
    <Box sx={{ pt: 2 }}>
      <Paper
        elevation={0}
        sx={{ border: 1, borderColor: "divider", borderRadius: BORDER_RADIUS.LARGE, overflow: "hidden" }}
        data-testid="audit-log"
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            Change history
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {created ? (
              <>
                Created {created}
                {createdBy ? (
                  <>
                    {" by "}
                    <PrincipalName value={createdBy} />
                  </>
                ) : null}
              </>
            ) : null}
            {modified && modified !== created ? (
              <>
                {" · Last changed "}
                {modified}
                {modifiedBy ? (
                  <>
                    {" by "}
                    <PrincipalName value={modifiedBy} />
                  </>
                ) : null}
              </>
            ) : null}
            {record?.deleted ? (
              <>
                {" · Deleted"}
                {deleted ? ` ${deleted}` : ""}
                {deletedBy ? (
                  <>
                    {" by "}
                    <PrincipalName value={deletedBy} />
                  </>
                ) : null}
              </>
            ) : null}
          </Typography>
        </Box>
        <Box sx={{ px: 2, py: 1.5 }}>
          {record?.id && resolvedTableName ? (
            <AuditTimeline tableName={resolvedTableName} recordId={String(record.id)} />
          ) : (
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              No record selected.
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
